import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileAudio, Check, X, AlertCircle, Search, Music, Drum, Waves, Zap } from 'lucide-react';

import { API_URL } from '../utils/api';

// Group icons
const GROUP_ICONS = {
  'Melodies': Music,
  'Drums': Drum,
  'Textures': Waves,
  'Sound FX': Zap
};

const LoopNamingDialog = ({ 
  isOpen, 
  onClose, 
  pairedFile, 
  onSubmit,
  existingNames = []
}) => {
  const [namingBanks, setNamingBanks] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedBaseName, setSelectedBaseName] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [collision, setCollision] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Pack assignment state (Phase 1)
  const [packCodes, setPackCodes] = useState([]);
  const [selectedPackCode, setSelectedPackCode] = useState('');
  const [newPackName, setNewPackName] = useState('');
  const [newPackBpm, setNewPackBpm] = useState('');
  const [showNewPackForm, setShowNewPackForm] = useState(false);

  // Fetch naming banks on mount
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/naming/banks`);
        if (response.ok) {
          const data = await response.json();
          setNamingBanks(data);
        }
      } catch (error) {
        console.error('Failed to fetch naming banks:', error);
      }
    };
    fetchBanks();
  }, []);
  
  // Fetch existing pack codes
  useEffect(() => {
    const fetchPackCodes = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/pack-codes`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setPackCodes(data);
        }
      } catch (error) {
        console.error('Failed to fetch pack codes:', error);
      }
    };
    if (isOpen) {
      fetchPackCodes();
    }
  }, [isOpen]);

  // Reset form when dialog opens with new file
  useEffect(() => {
    if (isOpen && pairedFile) {
      // Pre-populate instrument group if passed
      setSelectedGroup(pairedFile.instrument_group || '');
      
      // If a name was already assigned (from bulk naming), parse and use it
      if (pairedFile.base_name && pairedFile.base_name !== pairedFile.wav_file?.replace(/\.(wav|ogg|mp3)$/i, '')) {
        // Parse "Primary (Variant)" format
        const match = pairedFile.base_name.match(/^(.+?)\s*\((.+?)\)$/);
        if (match) {
          setSelectedBaseName(match[1]);
          setSelectedVariant(match[2]);
        } else {
          setSelectedBaseName(pairedFile.base_name);
          setSelectedVariant('');
        }
      } else {
        setSelectedBaseName('');
        setSelectedVariant('');
      }
      setSearchQuery('');
      setCollision(null);
      // Reset pack fields
      setSelectedPackCode('');
      setNewPackName('');
      setNewPackBpm('');
      setShowNewPackForm(false);
    }
  }, [isOpen, pairedFile]);

  // Auto-suggest default name when group is selected (only if no name already set)
  useEffect(() => {
    if (selectedGroup && namingBanks && !selectedBaseName) {
      const defaultName = namingBanks.default_suggestions[selectedGroup];
      setSelectedBaseName(defaultName || '');
      setSelectedVariant(''); // Reset variant when group changes
    }
  }, [selectedGroup, namingBanks, selectedBaseName]);

  // Build the final name
  const getFinalName = useCallback(() => {
    if (!selectedBaseName) return '';
    if (selectedVariant) {
      return `${selectedBaseName} (${selectedVariant})`;
    }
    return selectedBaseName;
  }, [selectedBaseName, selectedVariant]);

  // Check for collision when name changes
  useEffect(() => {
    const checkCollision = async () => {
      const finalName = getFinalName();
      if (!finalName || !selectedGroup) return;

      try {
        const response = await fetch(`${API_URL}/api/naming/check-collision`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: finalName, group: selectedGroup })
        });
        
        if (response.ok) {
          const data = await response.json();
          setCollision(data.collision ? data : null);
        }
      } catch (error) {
        console.error('Collision check failed:', error);
      }
    };

    const debounce = setTimeout(checkCollision, 300);
    return () => clearTimeout(debounce);
  }, [getFinalName, selectedGroup]);

  // Filter base names by search
  const getFilteredBaseNames = () => {
    if (!namingBanks || !selectedGroup) return [];
    const baseNames = namingBanks.banks[selectedGroup]?.base_names || [];
    if (!searchQuery) return baseNames;
    return baseNames.filter(name => 
      name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Get variants for current group
  const getVariants = () => {
    if (!namingBanks || !selectedGroup) return [];
    return namingBanks.banks[selectedGroup]?.variants || [];
  };
  
  // Generate pack code from name + BPM
  const generatePackCode = (name, bpm) => {
    const words = name.trim().split(/\s+/);
    const initials = words.map(w => w[0]?.toUpperCase() || '').join('').slice(0, 3);
    return `${initials}${bpm}`;
  };

  // Handle submit
  const handleSubmit = async () => {
    const finalName = getFinalName();
    
    if (!finalName) {
      toast.error('Please select a name');
      return;
    }

    if (!selectedGroup) {
      toast.error('Please select an instrument group');
      return;
    }

    if (collision) {
      toast.error('Name already exists. Please choose a different variant.');
      return;
    }
    
    // Determine pack info
    let packCode = null;
    let packName = null;
    
    if (showNewPackForm && newPackName && newPackBpm) {
      packCode = generatePackCode(newPackName, newPackBpm);
      packName = newPackName;
    } else if (selectedPackCode) {
      packCode = selectedPackCode;
      const existingPack = packCodes.find(p => p.pack_code === selectedPackCode);
      packName = existingPack?.pack_name || null;
    }

    setLoading(true);
    try {
      await onSubmit({
        finalName,
        group: selectedGroup,
        baseName: selectedBaseName,
        variant: selectedVariant,
        wavFile: pairedFile.wav_file,
        previewFile: pairedFile.preview_file,
        // Pack assignment fields
        pack_code: packCode,
        pack_name: packName
      });
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to create loop');
    } finally {
      setLoading(false);
    }
  };

  if (!namingBanks) {
    return null;
  }

  const GroupIcon = selectedGroup ? GROUP_ICONS[selectedGroup] : FileAudio;
  const finalName = getFinalName();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-white/10 max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileAudio className="w-5 h-5 text-primary" />
            Name Your Loop
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1 pr-2">
          {/* File Info */}
          {pairedFile && (
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-muted-foreground mb-1">Files:</p>
              <p className="text-sm font-mono">{pairedFile.wav_file}</p>
              <p className="text-sm font-mono text-muted-foreground">{pairedFile.preview_file}</p>
            </div>
          )}

          {/* Step 1: Select Group */}
          <div>
            <label className="block text-sm font-medium mb-2">
              1. Select Instrument Group <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {namingBanks.groups.map(group => {
                const Icon = GROUP_ICONS[group];
                return (
                  <button
                    key={group}
                    onClick={() => setSelectedGroup(group)}
                    className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                      selectedGroup === group
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{group}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Select Base Name */}
          {selectedGroup && (
            <div>
              <label className="block text-sm font-medium mb-2">
                2. Select Base Name <span className="text-destructive">*</span>
              </label>
              
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search names..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-xl bg-white/5 border-white/10"
                />
              </div>

              {/* Base Names Grid */}
              <div className="max-h-40 overflow-y-auto rounded-xl border border-white/10 p-2">
                <div className="grid grid-cols-3 gap-1">
                  {getFilteredBaseNames().map(name => (
                    <button
                      key={name}
                      onClick={() => setSelectedBaseName(name)}
                      className={`px-3 py-2 text-sm rounded-lg transition-all ${
                        selectedBaseName === name
                          ? 'bg-primary text-black font-medium'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                {getFilteredBaseNames().length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-4">
                    No names match your search
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Optional Variant */}
          {selectedBaseName && (
            <div>
              <label className="block text-sm font-medium mb-2">
                3. Add Variant (Optional)
              </label>
              
              {/* Variant Selection - Only from approved list */}
              <Select 
                value={selectedVariant || "_none"} 
                onValueChange={(v) => setSelectedVariant(v === "_none" ? "" : v)}
              >
                <SelectTrigger className="rounded-xl bg-white/5 border-white/10">
                  <SelectValue placeholder="Choose a variant..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No variant</SelectItem>
                  {getVariants().map(variant => (
                    <SelectItem key={variant} value={variant}>{variant}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <p className="text-xs text-muted-foreground mt-2">
                Variants help distinguish loops with the same base name
              </p>
            </div>
          )}
          
          {/* Step 4: Pack Assignment (Optional) */}
          {selectedBaseName && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
              <label className="block text-sm font-medium mb-2 text-amber-400">
                4. Assign to Pack (Optional)
              </label>
              
              {!showNewPackForm ? (
                <div className="space-y-3">
                  {/* Select existing pack */}
                  <Select 
                    value={selectedPackCode || "_none"} 
                    onValueChange={(v) => setSelectedPackCode(v === "_none" ? "" : v)}
                  >
                    <SelectTrigger className="rounded-xl bg-white/5 border-white/10" data-testid="pack-code-select">
                      <SelectValue placeholder="Select existing pack..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No pack assignment</SelectItem>
                      {packCodes.map(p => (
                        <SelectItem key={p.pack_code} value={p.pack_code}>
                          {p.pack_code} - {p.pack_name || 'Unnamed'} ({p.loop_count} loops)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-white/10"></div>
                    <span className="text-xs text-muted-foreground">or</span>
                    <div className="h-px flex-1 bg-white/10"></div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewPackForm(true)}
                    className="w-full rounded-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    data-testid="new-pack-btn"
                  >
                    + Create New Pack Code
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Pack Name</label>
                      <Input
                        placeholder="e.g., Neon Heir"
                        value={newPackName}
                        onChange={(e) => setNewPackName(e.target.value)}
                        className="rounded-lg bg-white/5 border-white/10"
                        data-testid="new-pack-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">BPM</label>
                      <Input
                        type="number"
                        placeholder="e.g., 90"
                        value={newPackBpm}
                        onChange={(e) => setNewPackBpm(e.target.value)}
                        className="rounded-lg bg-white/5 border-white/10"
                        data-testid="new-pack-bpm"
                      />
                    </div>
                  </div>
                  
                  {newPackName && newPackBpm && (
                    <div className="p-2 rounded-lg bg-amber-500/10 text-sm">
                      Pack Code: <span className="font-bold text-amber-400">{generatePackCode(newPackName, newPackBpm)}</span>
                    </div>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowNewPackForm(false);
                      setNewPackName('');
                      setNewPackBpm('');
                    }}
                    className="text-xs"
                  >
                    Cancel - Use Existing Pack
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-muted-foreground mt-3">
                Assign loops to a pack to quickly load them in the Pack Builder
              </p>
            </div>
          )}

          {/* Final Name Preview */}
          {finalName && (
            <div className={`p-4 rounded-xl border ${
              collision 
                ? 'border-destructive/50 bg-destructive/10' 
                : 'border-primary/50 bg-primary/10'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Final Loop Name:</p>
                  <p className="text-lg font-semibold">{finalName}</p>
                </div>
                {collision ? (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Name Exists
                  </Badge>
                ) : (
                  <Badge className="bg-primary/20 text-primary border-0 gap-1">
                    <Check className="w-3 h-3" />
                    Available
                  </Badge>
                )}
              </div>
              
              {collision && collision.suggested_variants?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-muted-foreground mb-2">Try these variants:</p>
                  <div className="flex flex-wrap gap-1">
                    {collision.suggested_variants.slice(0, 5).map(v => (
                      <button
                        key={v}
                        onClick={() => setSelectedVariant(v)}
                        className="px-2 py-1 text-xs rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-white/10 pt-4 mt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!finalName || !selectedGroup || collision || loading}
            className="rounded-full bg-primary hover:bg-primary/90 text-black"
          >
            {loading ? 'Creating...' : 'Create Loop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoopNamingDialog;
